import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
  CircularProgress,
  IconButton,
  Collapse,
  Chip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { MilestoneTask } from "../../features/taskReports/MilestoneTask";
import { ClockifyTimeEntry } from "../../features/clockifyTimeEntries/ClockifyTimeEntry";
import TimeFormatter from "../../features/extensions/TimeFormatter";

interface DeveloperDashboardProps {
  tasks: MilestoneTask[];
  timeEntries: ClockifyTimeEntry[];
  loading?: boolean;
  isExpanded?: boolean;
  showExpandButton?: boolean;
  onExpandClick?: () => void;
}

interface DeveloperSummary {
  name: string;
  hoursLogged: number;
  taskCount: number;
  taskBudget: number;
  efficiency: number | null;
}

function parseDeveloperNames(developerName: string): string[] {
  if (
    developerName.startsWith("[") &&
    developerName.endsWith("]")
  ) {
    try {
      const parsed = JSON.parse(developerName);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // fall through
    }
  }
  return [developerName];
}

function aggregateDeveloperData(
  tasks: MilestoneTask[],
  timeEntries: ClockifyTimeEntry[]
): DeveloperSummary[] {
  const devMap = new Map<
    string,
    { hoursLogged: number; taskIds: Set<number>; taskBudget: number }
  >();

  const getOrCreate = (name: string) => {
    let entry = devMap.get(name);
    if (!entry) {
      entry = { hoursLogged: 0, taskIds: new Set(), taskBudget: 0 };
      devMap.set(name, entry);
    }
    return entry;
  };

  // Hours from time entries
  timeEntries.forEach((te) => {
    if (te.developerName) {
      const entry = getOrCreate(te.developerName);
      entry.hoursLogged += te.timeSpentInHours;
    }
  });

  // Tasks + budget from task reports
  // Budget is split proportionally based on actual hours each developer logged on the task
  tasks.forEach((task) => {
    if (!task.taskReports || task.taskReports.length === 0) return;

    // Calculate hours per developer on this task
    const devHoursOnTask = new Map<string, number>();
    task.taskReports.forEach((report) => {
      if (report.developerName) {
        const devs = parseDeveloperNames(report.developerName);
        const timePerDev = report.totalTimeSpentOnTask / devs.length;
        devs.forEach((d) => {
          devHoursOnTask.set(d, (devHoursOnTask.get(d) || 0) + timePerDev);
        });
      }
    });

    if (devHoursOnTask.size === 0) return;

    const budget =
      typeof task.timeEstimate === "number" ? task.timeEstimate : 0;
    const totalHoursOnTask = Array.from(devHoursOnTask.values()).reduce(
      (sum, h) => sum + h,
      0
    );

    devHoursOnTask.forEach((devHours, devName) => {
      const entry = getOrCreate(devName);
      entry.taskIds.add(task.id);
      // Split budget proportionally to hours worked; fall back to equal split if no hours
      if (totalHoursOnTask > 0) {
        entry.taskBudget += budget * (devHours / totalHoursOnTask);
      } else {
        entry.taskBudget += budget / devHoursOnTask.size;
      }
    });
  });

  return Array.from(devMap.entries())
    .map(([name, data]) => ({
      name,
      hoursLogged: data.hoursLogged,
      taskCount: data.taskIds.size,
      taskBudget: data.taskBudget,
      efficiency:
        data.taskBudget > 0 ? data.hoursLogged / data.taskBudget : null,
    }))
    .sort((a, b) => b.hoursLogged - a.hoursLogged);
}

function getEfficiencyChip(efficiency: number | null) {
  if (efficiency === null) {
    return <Chip label="N/A" size="small" color="default" variant="outlined" />;
  }

  const label = `${efficiency.toFixed(2)}x`;

  if (efficiency <= 1.0) {
    return <Chip label={label} size="small" color="success" variant="outlined" />;
  }
  if (efficiency <= 1.3) {
    return <Chip label={label} size="small" color="warning" variant="outlined" />;
  }
  return <Chip label={label} size="small" color="error" variant="outlined" />;
}

const DeveloperDashboard = ({
  tasks,
  timeEntries,
  loading = false,
  isExpanded = true,
  showExpandButton = false,
  onExpandClick,
}: DeveloperDashboardProps) => {
  const theme = useTheme();
  const timeFormatter = new TimeFormatter();

  const developers = useMemo(
    () => aggregateDeveloperData(tasks, timeEntries),
    [tasks, timeEntries]
  );

  const hasData = developers.length > 0;

  const totals = useMemo(() => {
    if (!hasData) return null;
    return developers.reduce(
      (acc, dev) => ({
        hoursLogged: acc.hoursLogged + dev.hoursLogged,
        taskBudget: acc.taskBudget + dev.taskBudget,
      }),
      { hoursLogged: 0, taskBudget: 0 }
    );
  }, [developers, hasData]);

  const header = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
        px: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {showExpandButton && (
          <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )}
        <Typography variant="h6" component="h2">
          Developer Dashboard
        </Typography>
      </Box>
    </Box>
  );

  if (loading && !hasData) {
    return (
      <Box sx={{ overflowX: "auto" }}>
        {header}
        <Collapse in={isExpanded}>
          <Paper elevation={3} sx={{ mb: 3, overflow: "hidden" }}>
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          </Paper>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      {header}
      <Collapse in={isExpanded}>
        <Paper elevation={3} sx={{ mb: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table aria-label="developer dashboard table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 600,
                    }}
                  >
                    Developer
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 600,
                    }}
                  >
                    Hours Logged
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 600,
                    }}
                  >
                    Tasks
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 600,
                    }}
                  >
                    Task Budget
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 600,
                    }}
                  >
                    Efficiency
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasData ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No developer data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {developers.map((dev) => (
                      <TableRow
                        key={dev.name}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <Typography sx={{ fontWeight: 500 }}>
                            {dev.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{ fontWeight: 500, fontFamily: "monospace" }}
                          >
                            {timeFormatter.toHoursAndMinutes(dev.hoursLogged)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{ fontWeight: 500, fontFamily: "monospace" }}
                          >
                            {dev.taskCount}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{ fontWeight: 500, fontFamily: "monospace" }}
                          >
                            {dev.taskBudget > 0
                              ? timeFormatter.toHoursAndMinutes(dev.taskBudget)
                              : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {getEfficiencyChip(dev.efficiency)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {totals && (
                      <TableRow
                        sx={{
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                          "& td": { borderBottom: "none" },
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontFamily: "monospace",
                            }}
                          >
                            {timeFormatter.toHoursAndMinutes(totals.hoursLogged)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" />
                        <TableCell align="right">
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontFamily: "monospace",
                            }}
                          >
                            {totals.taskBudget > 0
                              ? timeFormatter.toHoursAndMinutes(totals.taskBudget)
                              : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {totals.taskBudget > 0
                            ? getEfficiencyChip(
                                totals.hoursLogged / totals.taskBudget
                              )
                            : getEfficiencyChip(null)}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default DeveloperDashboard;
